/**
 * FILE-OVERVIEW:
 * - 役割: メモ管理画面の統合コンテナ。検索・編集・出稿を担当。
 * - 主な連携: MemoCalendarMain.js, MemoContent.js, utils/newsmlBuilder.js
 * - 本番運用上の注意: 編集モーダルと出稿処理の入力項目対応を維持する。
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  ThemeProvider,
  Box,
  Dialog,
  DialogContent,
  Button,
  IconButton,
  Typography,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Pagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ArticleIcon from '@mui/icons-material/Article';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ClearIcon from '@mui/icons-material/Clear';
import useMediaQuery from '@mui/material/useMediaQuery';

import AppHeader from '../Common/AppHeader';
import MemoCalendarMain from './MemoCalendarMain';
import MemoNavigation from './MemoNavigation';
import MemoFilterSidebar from './MemoFilterSidebar';

import { theme, badgeColors } from '../../utils/colors';
import { listTodayContents, updateExistingMemoXml, groupContentsByTheme, uploadMemoXml, uploadMaterialFile, downloadMaterialFile, listMaterialFileMap, listArticleBodyMap } from '../../services/s3Service';
import { buildArticleNewsML2CTS, buildPhotoNewsML2CTS, buildPhotoEntityNewsML2CTS, buildArticleEntityNewsML2CTS } from '../../utils/newsmlBuilder';
import { loadCalendarEvents } from '../../services/calendarApi';

const ITEMS_PER_PAGE = 10;

// 重要度: 数値⇔ラベル変換
const PRIORITY_NUMBER_TO_LABEL = {
  '1': 'トップ', '3': '段もの', '5': 'ベタ', '6': '取り置き可', '7': 'その他', '8': '決まりもの',
};
const getPriorityLabel = (priority) => PRIORITY_NUMBER_TO_LABEL[priority] || priority || 'ベタ';

// 素材種別の正規化（数値→テキスト）
const MEMO_KIND_NORMALIZE = { '1': '記事', '2': '写真', '3': 'EPS', '4': 'PDF' };
const normalizeMemoKind = (kind) => MEMO_KIND_NORMALIZE[kind] || kind || '';

// 素材種別→スラッグ（ファイル名用）
const memoKindToSlug = (kind) => {
  const k = normalizeMemoKind(kind);
  if (k === '記事') return 'article';
  if (k === '写真') return 'photo';
  if (k === 'EPS' || k === 'ＥＰＳ') return 'eps';
  if (k === 'PDF' || k === 'ＰＤＦ') return 'pdf';
  return 'article';
};

// アプリ発番のChildMemoID（NOTEM-<ID>-<slug>-<連番>）からslug・連番を復元
// CTS発番のID（DT-DSK01M2026070314424331 等）は該当しないためnullを返す
const parseChildMemoId = (childMemoId) => {
  const match = /-(article|photo|eps|pdf)-(\d{4})$/.exec(childMemoId || '');
  return match ? { slug: match[1], serial: match[2] } : null;
};

// 記事本文の入力対象となる素材種別（エクセル・動画は本文入力なし）
const isArticleKind = (kind) => normalizeMemoKind(kind) === '記事';

// 素材種別（slug）ごとの添付可能ファイル形式（articleは添付なし）
// 写真はJPEGのみ許可（PNG/GIF等は不可）
const MATERIAL_FILE_ACCEPT_BY_SLUG = {
  photo: 'image/jpeg,.jpg,.jpeg',
  eps: '.eps,application/postscript',
  pdf: '.pdf,application/pdf',
};

// 写真素材の添付ファイル上限（10MB）
const PHOTO_MAX_FILE_SIZE = 10 * 1024 * 1024;

// 添付ファイルの検証（写真のみJPEG固定・10MB制限。エラー時はメッセージを返す）
const validateMaterialFile = (slug, file) => {
  if (slug !== 'photo' || !file) return '';
  const mimeType = (file.type || '').toLowerCase();
  const isJpeg = mimeType ? mimeType === 'image/jpeg' : /\.jpe?g$/i.test(file.name || '');
  if (!isJpeg) {
    return '写真の添付ファイルはJPEG形式（.jpg / .jpeg）のみアップロードできます。';
  }
  if (file.size > PHOTO_MAX_FILE_SIZE) {
    return `写真の添付ファイルは10MBまでです（選択されたファイル: ${(file.size / (1024 * 1024)).toFixed(1)}MB）。`;
  }
  return '';
};

// 画像ファイルの縦横ピクセルを取得（画像以外・取得失敗時は空オブジェクト）
const readImageSize = (file) => new Promise((resolve) => {
  if (!file?.type || !file.type.startsWith('image/')) {
    resolve({});
    return;
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const size = { imageWidth: img.naturalWidth, imageHeight: img.naturalHeight };
    URL.revokeObjectURL(url);
    resolve(size);
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    resolve({});
  };
  img.src = url;
});

// MemoIDからファイル名用IDを抽出（"-"でsplitして最後の値）
const extractMemoFileId = (memoId) => {
  if (!memoId) return '';
  const parts = memoId.split('-');
  return parts[parts.length - 1];
};

// 重要度: ラベル→数値変換（XML保存用）
const PRIORITY_LABEL_TO_NUMBER = {
  'トップ': '1', '段もの': '3', 'ベタ': '5', '取り置き可': '6', 'その他': '7', '決まりもの': '8',
};

// 日付文字列→date input用(YYYY-MM-DD)
const toDateInputFormat = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{8}$/.test(dateStr)) return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  const match = dateStr.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  return dateStr;
};

// 素材種別の表示名マッピング
const MEMO_KIND_LABELS = {
  '記事': '記 事',
  '写真': '写 真',
  'EPS': 'EPS',
  'PDF': 'PDF',
  '1': '記 事',
  '2': '写 真',
  '3': 'EPS',
  '4': 'PDF',
};

// 掲載状態の優先順位
const PUBLISHING_STATUS_PRIORITY = {
  '降版済み': 4,
  '掲載済み': 3,
  '出稿済み': 2,
  '未掲載': 1,
};

const aggregatePublishingStatus = (contents) => {
  let best = '';
  let bestPriority = 0;
  contents.forEach((c) => {
    const priority = PUBLISHING_STATUS_PRIORITY[c.publishingStatus] || 0;
    if (priority > bestPriority) {
      bestPriority = priority;
      best = c.publishingStatus;
    }
  });
  return best;
};

const aggregateMediaStatus = (contents) => {
  return contents.some((c) => c.mediaStatus === '派生済') ? '派生済' : '未派生';
};

// 全素材の掲載情報を集約
const aggregatePublishingInfoList = (contents) => {
  const allInfos = [];
  contents.forEach((c) => {
    if (c.publishingInfoList) {
      c.publishingInfoList.forEach((info) => {
        if (info.publishingDate || info.publishingPage) {
          allInfos.push(info);
        }
      });
    }
  });
  return allInfos;
};

const MemoPage = ({ user, isDesk, isReadOnly = false, onSignOut }) => {
  // レスポンシブ判定: モバイル/タブレット(～1023px) / PC(1024px～)
  const isMobile = useMediaQuery('(max-width:1023px)');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  // ChildMemoID→添付ファイルS3キーの対応表（photo/file/ の実ファイル一覧から構築）
  const [attachmentMap, setAttachmentMap] = useState({});
  // ChildMemoID→記事本文の対応表（article/file/ の実体NewsMLから構築）
  const [articleBodyMap, setArticleBodyMap] = useState({});
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listThemes, setListThemes] = useState([]);
  const [listPage, setListPage] = useState(1);
  const [openedFromList, setOpenedFromList] = useState(false);
  const calendarRef = useRef(null);

  // 全コンテンツ（S3から取得）
  const [allContents, setAllContents] = useState([]);
  const [loading, setLoading] = useState(true);

  // ドロワー開閉状態
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // 自動更新（両画面共通でローカルストレージ管理）
  const [autoUpdate, setAutoUpdateState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('shared_autoUpdate')) || false; } catch { return false; }
  });
  const setAutoUpdate = (value) => {
    setAutoUpdateState(value);
    localStorage.setItem('shared_autoUpdate', JSON.stringify(value));
  };

  // フィルター状態（OFFにしたものをローカルストレージで管理、初期は全ON）
  const [disabledNewsGenres, setDisabledNewsGenres] = useState(() => {
    try { return JSON.parse(localStorage.getItem('memo_disabledNewsGenres')) || []; } catch { return []; }
  });
  const [disabledMediaStatuses, setDisabledMediaStatuses] = useState(() => {
    try { return JSON.parse(localStorage.getItem('memo_disabledMediaStatuses')) || []; } catch { return []; }
  });
  const [disabledPublishingStatuses, setDisabledPublishingStatuses] = useState(() => {
    try { return JSON.parse(localStorage.getItem('memo_disabledPublishingStatuses')) || []; } catch { return []; }
  });

  // S3からデータ取得
  useEffect(() => {
    const fetchContents = async () => {
      setLoading(true);
      try {
        const contents = await listTodayContents();
        setAllContents(contents);
      } catch (error) {
        console.error('素材情報の取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContents();
  }, []);

  // 自動更新（5秒間隔）
  useEffect(() => {
    if (!autoUpdate) return;
    const intervalId = setInterval(async () => {
      try {
        const contents = await listTodayContents();
        setAllContents(contents);
      } catch (error) {
        console.error('自動更新中にエラー:', error);
      }
    }, 5000);
    return () => clearInterval(intervalId);
  }, [autoUpdate]);

  // 分類コード↔ラベルのマッピング
  const SUBJECT_CODE_MAP = [
    { code: '01000000', label: '文化' },
    { code: '02000000', label: '事件・司法' },
    { code: '03000000', label: '災害・事故' },
    { code: '04000000', label: '経済・産業' },
    { code: '05000000', label: '教育' },
    { code: '06000000', label: '環境問題' },
    { code: '07000000', label: '保険衛生' },
    { code: '08000000', label: '人間的興味' },
    { code: '09000000', label: '労働問題' },
    { code: '10000000', label: 'ライフスタイル・レジャー' },
    { code: '11000000', label: '政治' },
    { code: '12000000', label: '宗教・思想' },
    { code: '13000000', label: '科学・テクノロジー' },
    { code: '14000000', label: '社会問題' },
    { code: '15000000', label: 'スポーツ' },
    { code: '16000000', label: '社会不安・紛争・戦争' },
    { code: '17000000', label: '気象' },
  ];

  // フィルターオプション（固定値 + データから動的取得）
  const FIXED_NEWS_GENRES = ['社会', '政治', '経済', '地域', '運動', '文化', '天鐘', '後日掲載'];
  const FIXED_MEDIA_STATUSES = ['未派生', '派生済'];
  const FIXED_PUBLISHING_STATUSES = ['未掲載', '掲載済み', '降版済み', '出稿済み'];

  const OTHER_GENRE_LABEL = 'その他';

  const newsGenreOptions = useMemo(() => {
    return [...FIXED_NEWS_GENRES, OTHER_GENRE_LABEL];
  }, []);

  const mediaStatusOptions = useMemo(() => {
    const values = new Set(FIXED_MEDIA_STATUSES);
    allContents.forEach((c) => {
      values.add(c.mediaStatus || '(未設定)');
    });
    return [...values];
  }, [allContents]);

  const publishingStatusOptions = useMemo(() => {
    const values = new Set(FIXED_PUBLISHING_STATUSES);
    allContents.forEach((c) => {
      values.add(c.publishingStatus || '(未設定)');
    });
    return [...values];
  }, [allContents]);

  // OFFリストから選択中リストを算出
  const selectedNewsGenres = useMemo(() => newsGenreOptions.filter(v => !disabledNewsGenres.includes(v)), [newsGenreOptions, disabledNewsGenres]);
  const selectedMediaStatuses = useMemo(() => mediaStatusOptions.filter(v => !disabledMediaStatuses.includes(v)), [mediaStatusOptions, disabledMediaStatuses]);
  const selectedPublishingStatuses = useMemo(() => publishingStatusOptions.filter(v => !disabledPublishingStatuses.includes(v)), [publishingStatusOptions, disabledPublishingStatuses]);

  // OFFリストのセッター（選択中リストを受け取り、OFFリストに変換して保存）
  const setSelectedNewsGenres = (selected) => {
    const disabled = newsGenreOptions.filter(v => !selected.includes(v));
    setDisabledNewsGenres(disabled);
    localStorage.setItem('memo_disabledNewsGenres', JSON.stringify(disabled));
  };
  const setSelectedMediaStatuses = (selected) => {
    const disabled = mediaStatusOptions.filter(v => !selected.includes(v));
    setDisabledMediaStatuses(disabled);
    localStorage.setItem('memo_disabledMediaStatuses', JSON.stringify(disabled));
  };
  const setSelectedPublishingStatuses = (selected) => {
    const disabled = publishingStatusOptions.filter(v => !selected.includes(v));
    setDisabledPublishingStatuses(disabled);
    localStorage.setItem('memo_disabledPublishingStatuses', JSON.stringify(disabled));
  };

  // フィルター済みコンテンツ
  const filteredContents = useMemo(() => {
    return allContents.filter((content) => {
      const genre = content.newsGenre || '(未設定)';
      const media = content.mediaStatus || '(未設定)';
      const publishing = content.publishingStatus || '(未設定)';
      // FIXED_NEWS_GENRESに含まれないジャンルは「その他」として判定
      const genreForFilter = FIXED_NEWS_GENRES.includes(genre) ? genre : OTHER_GENRE_LABEL;
      return (
        selectedNewsGenres.includes(genreForFilter) &&
        selectedMediaStatuses.includes(media) &&
        selectedPublishingStatuses.includes(publishing)
      );
    });
  }, [allContents, selectedNewsGenres, selectedMediaStatuses, selectedPublishingStatuses]);

  // テーマ詳細モーダルを開いたら、photo/file/・article/file/ の実体NewsMLから
  // 添付ファイル対応表・記事本文対応表を構築
  useEffect(() => {
    if (!detailModalOpen || !selectedTheme?.memoId) return;
    let mounted = true;
    const memoFileId = extractMemoFileId(selectedTheme.memoId);
    listMaterialFileMap(memoFileId)
      .then((map) => { if (mounted) setAttachmentMap(map); })
      .catch((e) => {
        console.warn('添付ファイル対応表の取得に失敗:', e);
        if (mounted) setAttachmentMap({});
      });
    listArticleBodyMap(memoFileId)
      .then((map) => { if (mounted) setArticleBodyMap(map); })
      .catch((e) => {
        console.warn('記事本文対応表の取得に失敗:', e);
        if (mounted) setArticleBodyMap({});
      });
    return () => { mounted = false; };
  }, [detailModalOpen, selectedTheme?.memoId]);

  // 現在のデータから作ったテーマ一覧（保存後の再取得を常に反映させるための解決元）
  const liveThemes = useMemo(() => groupContentsByTheme(filteredContents), [filteredContents]);

  // 一覧モーダルは開いた時点のスナップショットを持つため、最新データで解決し直す
  // （素材が全て削除されたテーマは一覧からも消える）
  const liveListThemes = useMemo(
    () => listThemes
      .map((t) => liveThemes.find((x) => x.memoId === t.memoId))
      .filter(Boolean),
    [listThemes, liveThemes]
  );

  // テーマクリック時（カレンダーから直接）
  const handleDateSelect = (date, themeData) => {
    setSelectedDate(date);
    if (themeData) {
      setSelectedTheme(themeData);
      setOpenedFromList(false);
      setDetailModalOpen(true);
    }
  };

  // 「+N件」クリック時
  const handleShowMore = (date, themes) => {
    setSelectedDate(date);
    setListThemes(themes);
    setListPage(1);
    setListModalOpen(true);
  };

  // 一覧からテーマを選択（一覧が保持する古いスナップショットではなく最新データを開く）
  const handleSelectFromList = (themeData) => {
    setSelectedTheme(liveThemes.find((t) => t.memoId === themeData.memoId) || themeData);
    setOpenedFromList(true);
    setListModalOpen(false);
    setDetailModalOpen(true);
  };

  // === 編集モード ===
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEditing = () => {
    if (isReadOnly) {
      alert('このユーザー権限ではメモを編集できません（閲覧のみ）。');
      return;
    }

    if (!selectedTheme) return;
    const contents = selectedTheme.contents || [];
    setEditData({
      themeName: selectedTheme.themeName || '',
      themeComment: selectedTheme.themeComment || '',
      newsGenre: selectedTheme.newsGenre || '',
      category: (() => {
        const raw = selectedTheme.contents?.[0]?.subjectCode1 || '';
        // コード値ならそのまま、ラベル名ならコードに変換
        const byCode = SUBJECT_CODE_MAP.find(item => item.code === raw);
        if (byCode) return raw;
        const byLabel = SUBJECT_CODE_MAP.find(item => item.label === raw);
        return byLabel ? byLabel.code : raw;
      })(),
      priority: getPriorityLabel(selectedTheme.priority),
      registrationDate: toDateInputFormat(selectedTheme.initialRegistrationDate),
      materials: contents.map(c => ({
        memoKind: normalizeMemoKind(c.memoKind),
        headline: c.headLine || '',
        editInfo: c.editInfo || '',
        linkUrl: c.linkUrl || '',
        registrationDate: toDateInputFormat(c.registrationDate),
        childMemoId: c.childMemoId || '',
        // 既存添付は photo/file/ の実体NewsMLから構築した対応表で解決する
        materialFileKey: attachmentMap[c.childMemoId] || '',
        file: null,
        // 既存の記事本文は article/file/ の実体NewsMLから構築した対応表で解決する
        articleBody: articleBodyMap[c.childMemoId] || '',
      })),
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData(null);
  };

  // 編集終了（保存・キャンセル）とクリックが競合するとprevがnullになりうるため必ずガードする
  const updateEditMaterial = (idx, key, value) => {
    setEditData(prev => prev ? ({
      ...prev,
      materials: prev.materials.map((m, i) => i === idx ? { ...m, [key]: value } : m),
    }) : prev);
  };

  const addNewMaterial = () => {
    setEditData(prev => prev ? ({
      ...prev,
      materials: [
        ...prev.materials,
        {
          memoKind: '記事',
          headline: '',
          editInfo: '',
          linkUrl: '',
          registrationDate: prev.registrationDate || '',
          childMemoId: '',
          isNew: true,
          materialFileKey: '',
          file: null,
          articleBody: '',
        },
      ],
    }) : prev);
  };

  const removeEditMaterial = (idx) => {
    setEditData(prev => {
      if (!prev || prev.materials.length <= 1) return prev;
      return {
        ...prev,
        materials: prev.materials.filter((_, i) => i !== idx),
      };
    });
  };

  const handleSaveEdit = async () => {
    if (isReadOnly) {
      alert('このユーザー権限では保存できません（閲覧のみ）。');
      return;
    }

    if (!editData || !selectedTheme) return;

    // 添付ファイルの検証（写真はJPEG固定・10MBまで。種別変更後の不整合もここで弾く）
    const fileError = editData.materials
      .map((m) => validateMaterialFile(memoKindToSlug(m.memoKind), m.file))
      .find(Boolean);
    if (fileError) {
      alert(fileError);
      return;
    }

    setSaving(true);
    try {
      // 0. 新規素材にchildMemoIdを生成
      const fileId = extractMemoFileId(selectedTheme.memoId);
      const existingSlugs = {};
      // 既存素材のslug別最大連番を求める
      (selectedTheme.contents || []).forEach(c => {
        const match = c.childMemoId?.match(/-(article|photo|eps|pdf)-(\d{4})$/);
        if (match) {
          const slug = match[1];
          const serial = parseInt(match[2], 10);
          existingSlugs[slug] = Math.max(existingSlugs[slug] || 0, serial);
        }
      });

      const materialsWithIds = editData.materials.map(m => {
        if (m.childMemoId) return m;
        // 新規素材: childMemoIdを生成
        const slug = memoKindToSlug(m.memoKind);
        existingSlugs[slug] = (existingSlugs[slug] || 0) + 1;
        const serial = String(existingSlugs[slug]).padStart(4, '0');
        return { ...m, childMemoId: `NOTEM-${fileId}-${slug}-${serial}` };
      });

      // 削除された素材のchildMemoIdリスト
      const originalChildIds = (selectedTheme.contents || []).map(c => c.childMemoId).filter(Boolean);
      const currentChildIds = materialsWithIds.map(m => m.childMemoId).filter(Boolean);
      const deletedChildMemoIds = originalChildIds.filter(id => !currentChildIds.includes(id));

      // 0b. 再出稿ファイル名を先に確定する（添付ファイルのS3キーをテーマXML更新に含めるため）
      // ファイル名: <カレンダー日付>_<登録日時>_memo_<ID>-<slug>_<連番>

      // DynamoDBスケジュールからカレンダー日付・ByLine・CreatorIDを取得
      let calendarDate = (selectedTheme.dateKey || '').replace(/-/g, '');
      let scheduleDate = selectedTheme.dateKey; // eventStart用フォールバック
      let reporterName = '';
      let creatorId = 'system';
      try {
        const schedules = await loadCalendarEvents();
        const matched = schedules.find(s => s.id && s.id.endsWith(fileId));
        if (matched) {
          if (matched.date) {
            calendarDate = matched.date.replace(/-/g, '');
            scheduleDate = matched.date;
          }
          reporterName = matched.person || '';
          creatorId = matched.createdByUsername || 'system';
        }
      } catch (e) {
        console.warn('スケジュール取得失敗、フォールバック使用:', e);
      }

      const regDate = toDateInputFormat(selectedTheme.initialRegistrationDate).replace(/-/g, '');

      // ファイル名用のslug別連番。
      // 素材の並び順はCTS側の再生成のたびに変わるため、順番からは採番しない。
      // ChildMemoIDが連番を持つ素材はその値を維持し（＝同じ素材は常に同じファイル名で上書き）、
      // 持たない素材（CTS発番ID）にのみ空き番号を割り当てる。
      const usedSerialsBySlug = {};
      const reserveSerial = (slug, serial) => {
        if (!usedSerialsBySlug[slug]) usedSerialsBySlug[slug] = new Set();
        usedSerialsBySlug[slug].add(serial);
      };
      const takeNextSerial = (slug) => {
        if (!usedSerialsBySlug[slug]) usedSerialsBySlug[slug] = new Set();
        let n = 1;
        while (usedSerialsBySlug[slug].has(String(n).padStart(4, '0'))) n++;
        const serial = String(n).padStart(4, '0');
        usedSerialsBySlug[slug].add(serial);
        return serial;
      };
      // 先に既存IDが持つ連番を確保してから、残りへ採番する
      materialsWithIds.forEach(m => {
        const parsed = parseChildMemoId(m.childMemoId);
        if (parsed && parsed.slug === memoKindToSlug(m.memoKind)) reserveSerial(parsed.slug, parsed.serial);
      });

      const materialsPrepared = materialsWithIds.map(m => {
        // 編集後の素材種別からslugを決定
        const fileSlug = memoKindToSlug(m.memoKind);
        const parsed = parseChildMemoId(m.childMemoId);
        // 種別が変わっていない既存素材は元の連番を維持する
        const fileSerial = (parsed && parsed.slug === fileSlug) ? parsed.serial : takeNextSerial(fileSlug);
        const isArticle = fileSlug === 'article';

        const baseFileName = `${calendarDate}_${regDate}_memo_${fileId}-${fileSlug}_${fileSerial}`;

        // 記事本文: 「記事」種別のみ対象。本文が入力済み、または既存本文の削除時に実体を出稿する
        const isArticleBodyTarget = isArticleKind(m.memoKind)
          && ((m.articleBody || '').trim() !== '' || !!articleBodyMap[m.childMemoId]);

        // 添付ファイルのS3キー: 新規添付があれば新キー、なければ既存キーを引き継ぐ
        let materialFileKey = m.materialFileKey || '';
        if (m.file && !isArticle) {
          const extMatch = /\.[^.]+$/.exec(m.file.name || '');
          const ext = extMatch ? extMatch[0].toLowerCase() : '';
          materialFileKey = `photo/file/${baseFileName}${ext}`;
        }

        return { ...m, fileSlug, fileSerial, isArticle, isArticleBodyTarget, baseFileName, materialFileKey };
      });

      // 1. 既存XMLの更新（cts2app）— テーマに紐づく全素材ファイルを更新
      const updatePayload = {
        memoId: selectedTheme.memoId,
        themeName: editData.themeName,
        themeComment: editData.themeComment,
        newsGenre: editData.newsGenre,
        category: editData.category,
        priority: PRIORITY_LABEL_TO_NUMBER[editData.priority] || editData.priority,
        registrationDate: editData.registrationDate?.replace(/-/g, '') || '',
        materials: materialsPrepared.filter(m => !m.isNew).map(m => ({
          childMemoId: m.childMemoId,
          memoKind: m.memoKind,
          headline: m.headline,
          editInfo: m.editInfo,
          linkUrl: m.linkUrl,
          registrationDate: m.registrationDate?.replace(/-/g, '') || '',
        })),
        newMaterials: materialsPrepared.filter(m => m.isNew).map(m => ({
          childMemoId: m.childMemoId,
          memoKind: m.memoKind,
          headline: m.headline,
          editInfo: m.editInfo,
          linkUrl: m.linkUrl,
          registrationDate: m.registrationDate?.replace(/-/g, '') || '',
        })),
        deletedChildMemoIds,
      };

      // テーマに紐づく全素材のsourceFileキーを収集（重複排除）
      const fileKeys = [...new Set(
        selectedTheme.contents
          .map(c => c.sourceFile?.key)
          .filter(Boolean)
      )];
      if (fileKeys.length === 0) throw new Error('元ファイルの情報が見つかりません');

      for (const fileKey of fileKeys) {
        await updateExistingMemoXml(fileKey, updatePayload);
      }

      // 2. 再出稿（app2cts）— 編集内容で素材ごとにXMLを生成・アップロード
      for (const m of materialsPrepared) {
        const selectedMemoType = String(editData.newsGenre ?? '').replace(/　/g, ' ').trim() || 'ＮＥＣ';

        const buildParams = {
          existingThemeDuid: selectedTheme.memoId,
          existingNewsItemId: m.childMemoId,
          themeName: editData.themeName,
          themeContent: editData.themeComment,
          memoPriority: editData.priority,
          memoRegistrationDate: editData.registrationDate,
          memoCategory: editData.category,
          materialHeadline: m.headline,
          materialKind: m.memoKind,
          memoType: selectedMemoType,
          eventStart: scheduleDate,
          reporterName,
          username: creatorId,
          linkUrl: m.linkUrl,
          editInfo: m.editInfo,
        };

        const xmlString = m.isArticle
          ? buildArticleNewsML2CTS(buildParams)
          : buildPhotoNewsML2CTS({
              ...buildParams,
              entityFileName: m.materialFileKey.split('/').pop(),
            });

        const folder = m.isArticle ? 'article/memo' : 'photo/memo';
        const s3Key = `${folder}/${m.baseFileName}.xml`;

        await uploadMemoXml(xmlString, s3Key);

        // 3. 新規添付ファイルがあれば実体（Version1）セットを photo/file/ へアップロード
        //    - 実体ファイル本体（メモXMLと同名・拡張子のみ差し替え）
        //    - 実体NewsML（ContentItem Href で実体ファイル名を参照、仕様書準拠）
        if (m.file && !m.isArticle) {
          await uploadMaterialFile(m.file, m.materialFileKey);

          const { imageWidth, imageHeight } = await readImageSize(m.file);
          const entityXml = buildPhotoEntityNewsML2CTS({
            memoPriority: editData.priority,
            reporterName,
            username: creatorId,
            materialHeadline: m.headline,
            memoType: selectedMemoType,
            memoId: selectedTheme.memoId,
            childMemoId: m.childMemoId,
            linkUrl: m.linkUrl,
            editInfo: m.editInfo,
            memoCategory: editData.category,
            entityFileName: m.materialFileKey.split('/').pop(),
            imageWidth,
            imageHeight,
          });
          await uploadMemoXml(entityXml, `photo/file/${m.baseFileName}.xml`);
        }

        // 4. 記事本文があれば実体（Version1）NewsMLを article/file/ へアップロード
        //    - 本文は ContentItem/DataContent にプレーンテキストで格納（仕様書準拠）
        if (m.isArticleBodyTarget) {
          const articleEntityXml = buildArticleEntityNewsML2CTS({
            memoPriority: editData.priority,
            reporterName,
            username: creatorId,
            materialHeadline: m.headline,
            memoType: selectedMemoType,
            memoId: selectedTheme.memoId,
            childMemoId: m.childMemoId,
            linkUrl: m.linkUrl,
            editInfo: m.editInfo,
            memoCategory: editData.category,
            articleBody: m.articleBody || '',
          });
          await uploadMemoXml(articleEntityXml, `article/file/${m.baseFileName}.xml`);
        }
      }

      setIsEditing(false);
      setEditData(null);

      // データ再取得
      const contents = await listTodayContents();
      setAllContents(contents);

      // selectedTheme を再取得データで更新
      const themes = groupContentsByTheme(contents);
      const updated = themes.find(t => t.memoId === selectedTheme.memoId);
      if (updated) {
        setSelectedTheme(updated);
      }

      // 添付ファイル対応表・記事本文対応表も更新（今回アップロードした実体を反映）
      try {
        setAttachmentMap(await listMaterialFileMap(fileId));
      } catch (e) {
        console.warn('添付ファイル対応表の再取得に失敗:', e);
      }
      try {
        setArticleBodyMap(await listArticleBodyMap(fileId));
      } catch (e) {
        console.warn('記事本文対応表の再取得に失敗:', e);
      }

      alert('メモを更新・再出稿しました');
    } catch (error) {
      console.error('メモ更新・再出稿エラー:', error);
      alert(`メモの更新・再出稿中にエラーが発生しました。\n詳細: ${error?.message || '不明なエラー'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedTheme(null);
    setIsEditing(false);
    setEditData(null);
    if (openedFromList) {
      setOpenedFromList(false);
      setListModalOpen(true);
    }
  };

  const handleCloseListModal = () => {
    setListModalOpen(false);
    setListThemes([]);
  };

  const handleListPageChange = (event, value) => {
    setListPage(value);
  };

  // 日付フォーマット
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (/^\d{8}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${year}/${month}/${day}`;
    }
    if (/^\d{14}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const min = dateStr.substring(10, 12);
      const sec = dateStr.substring(12, 14);
      return `${year}/${month}/${day} ${hour}:${min}:${sec}`;
    }
    return dateStr;
  };

  // 全角英数字・記号を半角に変換（漢字・かなはそのまま）
  const toHalfWidth = (str) => {
    if (!str) return str;
    return String(str)
      .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
      .replace(/　/g, ' ');
  };

  // 素材種別の表示ラベル取得
  const getMemoKindLabel = (memoKind) => {
    return MEMO_KIND_LABELS[memoKind] || memoKind || '-';
  };

  // バッジスタイル共通
  const statusBadgeStyle = {
    display: 'inline-block',
    fontSize: '0.8rem',
    lineHeight: 1.3,
    padding: '3px 10px',
    borderRadius: '4px',
    color: '#fff',
    fontWeight: 'bold',
    letterSpacing: '0.15em',
  };

  // 素材種別バッジスタイル
  const memoKindBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    lineHeight: 1.3,
    padding: '3px 10px',
    borderRadius: '4px',
    color: '#fff',
    fontWeight: 'bold',
    backgroundColor: badgeColors.newsGenre,
    letterSpacing: '0.15em',
    minWidth: '50px',
  };

  // 一覧ページネーション
  const totalPages = Math.ceil(liveListThemes.length / ITEMS_PER_PAGE);
  const startIndex = (listPage - 1) * ITEMS_PER_PAGE;
  const currentListThemes = liveListThemes.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="memo-root-layout"
        sx={{
          maxWidth: 1920,
          width: '100%',
          mx: 'auto',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        <AppHeader
          isSidebarOpen={isDrawerOpen}
          onMenuToggle={setIsDrawerOpen}
        />

        <Box
          className="memo-main-area"
          sx={{
            maxWidth: 1920,
            width: '100%',
            mx: 'auto',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <MemoCalendarMain
            isMobile={isMobile}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onShowMore={handleShowMore}
            calendarRef={calendarRef}
            filteredContents={filteredContents}
            loading={loading}
          />

          <MemoFilterSidebar
            isMobile={isMobile}
            isDrawerOpen={isDrawerOpen}
            onDrawerClose={() => setIsDrawerOpen(false)}
            user={user}
            isDesk={isDesk}
            onSignOut={onSignOut}
            autoUpdate={autoUpdate}
            setAutoUpdate={setAutoUpdate}
            newsGenreOptions={newsGenreOptions}
            selectedNewsGenres={selectedNewsGenres}
            setSelectedNewsGenres={setSelectedNewsGenres}
            mediaStatusOptions={mediaStatusOptions}
            selectedMediaStatuses={selectedMediaStatuses}
            setSelectedMediaStatuses={setSelectedMediaStatuses}
            publishingStatusOptions={publishingStatusOptions}
            selectedPublishingStatuses={selectedPublishingStatuses}
            setSelectedPublishingStatuses={setSelectedPublishingStatuses}
          />
        </Box>

        {isMobile && <MemoNavigation calendarRef={calendarRef} />}

        {/* テーマ一覧モーダル */}
        <Dialog
          open={listModalOpen}
          onClose={handleCloseListModal}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArticleIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                テーマ一覧
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({liveListThemes.length}件)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseListModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent dividers sx={{ p: 0 }}>
            <List disablePadding>
              {currentListThemes.map((themeData, index) => (
                <ListItemButton
                  key={`${themeData.memoId}-${startIndex + index}`}
                  onClick={() => handleSelectFromList(themeData)}
                  divider
                >
                  <ListItemIcon>
                    <NewspaperIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={themeData.themeName || '(テーマ名なし)'}
                    secondary={themeData.newsGenre ? `種別: ${themeData.newsGenre}` : null}
                    primaryTypographyProps={{
                      noWrap: true,
                      sx: { fontWeight: 500 }
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination
                  count={totalPages}
                  page={listPage}
                  onChange={handleListPageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <Button onClick={handleCloseListModal} variant="outlined">
              閉じる
            </Button>
          </Box>
        </Dialog>

        {/* メモ詳細ダイアログ */}
        <Dialog
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: { maxHeight: '90vh' }
          }}
        >
          {selectedTheme && (() => {
            const contents = selectedTheme.contents || [];
            const pubStatus = aggregatePublishingStatus(contents);
            const mediaStatus = aggregateMediaStatus(contents);

            return (
              <DialogContent sx={{ p: 0 }}>
                {/* ヘッダー部分 */}
                <Box sx={{
                  p: 2,
                  pb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                }}>
                  {/* 左: ステータスバッジ */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {selectedTheme.newsGenre && (
                      <span style={{
                        ...statusBadgeStyle,
                        backgroundColor: badgeColors.newsGenre,
                      }}>
                        {selectedTheme.newsGenre}
                      </span>
                    )}
                    {pubStatus && (
                      <span style={{
                        ...statusBadgeStyle,
                        backgroundColor: badgeColors.publishingStatus,
                      }}>
                        {pubStatus}
                      </span>
                    )}
                    {mediaStatus && mediaStatus !== '未派生' && (
                      <span style={{
                        ...statusBadgeStyle,
                        backgroundColor: badgeColors.mediaStatus,
                        color: '#404048',
                      }}>
                        {mediaStatus}
                      </span>
                    )}
                  </Box>

                  {/* 右: 編集/閉じるボタン */}
                  <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {isEditing ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={cancelEditing}
                          disabled={saving}
                        >
                          キャンセル
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={handleSaveEdit}
                          disabled={saving}
                          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        >
                          保存
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={startEditing}
                          startIcon={<EditIcon />}
                          disabled={isReadOnly}
                        >
                          編集
                        </Button>
                        <IconButton onClick={handleCloseDetailModal} size="small">
                          <CloseIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>

                {/* テーマ情報 */}
                <Box sx={{ px: 2, pb: 2 }}>
                  {isEditing && editData ? (
                    <>
                      {/* === 編集モード === */}
                      <TextField
                        label="テーマ名"
                        fullWidth
                        size="small"
                        value={editData.themeName}
                        onChange={(e) => setEditData(prev => prev ? ({ ...prev, themeName: e.target.value }) : prev)}
                        sx={{ mb: 1.5 }}
                      />

                      <TextField
                        label="テーマの内容"
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        value={editData.themeComment}
                        onChange={(e) => setEditData(prev => prev ? ({ ...prev, themeComment: e.target.value }) : prev)}
                        sx={{ mb: 1.5 }}
                      />

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1.5 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel shrink>種別</InputLabel>
                          <Select
                            label="種別"
                            value={editData.newsGenre}
                            onChange={(e) => setEditData(prev => prev ? ({ ...prev, newsGenre: e.target.value }) : prev)}
                            displayEmpty
                            renderValue={(v) => v || ''}
                          >
                            <MenuItem value="　">　</MenuItem>
                            <MenuItem value="経済">経済</MenuItem>
                            <MenuItem value="社会">社会</MenuItem>
                            <MenuItem value="政治">政治</MenuItem>
                            <MenuItem value="地域">地域</MenuItem>
                            <MenuItem value="ひろば">ひろば</MenuItem>
                            <MenuItem value="運動">運動</MenuItem>
                            <MenuItem value="文化">文化</MenuItem>
                            <MenuItem value="天鐘">天鐘</MenuItem>
                            <MenuItem value="自動組">自動組</MenuItem>
                            <MenuItem value="シス用">シス用</MenuItem>
                            <MenuItem value="写真">写真</MenuItem>
                            <MenuItem value="線画">線画</MenuItem>
                            <MenuItem value="ＥＰＳ">ＥＰＳ</MenuItem>
                            <MenuItem value="ＰＤＦ">ＰＤＦ</MenuItem>
                            <MenuItem value="大組">大組</MenuItem>
                            <MenuItem value="箱組">箱組</MenuItem>
                            <MenuItem value="DB出">DB出</MenuItem>
                            <MenuItem value="ＮＥＣ">ＮＥＣ</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <InputLabel shrink>分類</InputLabel>
                          <Select
                            label="分類"
                            value={editData.category}
                            onChange={(e) => setEditData(prev => prev ? ({ ...prev, category: e.target.value }) : prev)}
                            displayEmpty
                            renderValue={(v) => {
                              const found = SUBJECT_CODE_MAP.find(item => item.code === v);
                              return found ? found.label : v || '';
                            }}
                          >
                            <MenuItem value="　">　</MenuItem>
                            {SUBJECT_CODE_MAP.map(item => (
                              <MenuItem key={item.code} value={item.code}>{item.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1.5 }}>
                        <TextField
                          label="登録予定日"
                          type="date"
                          fullWidth
                          size="small"
                          value={editData.registrationDate}
                          onChange={(e) => setEditData(prev => prev ? ({ ...prev, registrationDate: e.target.value }) : prev)}
                          InputLabelProps={{ shrink: true }}
                        />

                        <FormControl fullWidth size="small">
                          <InputLabel>重要度</InputLabel>
                          <Select
                            label="重要度"
                            value={editData.priority}
                            onChange={(e) => setEditData(prev => prev ? ({ ...prev, priority: e.target.value }) : prev)}
                          >
                            <MenuItem value="ベタ">ベタ</MenuItem>
                            <MenuItem value="トップ">トップ</MenuItem>
                            <MenuItem value="段もの">段もの</MenuItem>
                            <MenuItem value="取り置き可">取り置き可</MenuItem>
                            <MenuItem value="その他">その他</MenuItem>
                            <MenuItem value="決まりもの">決まりもの</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          素材一覧
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={addNewMaterial}
                          disabled={saving}
                        >
                          素材追加
                        </Button>
                      </Box>

                      {editData.materials.map((m, idx) => (
                        <Box
                          key={`${m.childMemoId || 'new'}-${idx}`}
                          sx={{
                            mb: 2,
                            pb: 2,
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          {/* ヘッダー: 素材番号・種別・削除ボタン */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              素材 {idx + 1} ・ {m.memoKind}
                            </Typography>
                            {editData.materials.length > 1 && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeEditMaterial(idx)}
                                title="この素材を削除"
                                disabled={saving}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>

                          {/* 1行目: 素材種別 / 登録予定日 */}
                          {/* TODO: 行数フィールド（XMLにタグが存在しないため一旦非表示）
                            <TextField
                              label="行数"
                              fullWidth
                              size="small"
                              type="number"
                              inputProps={{ min: 0 }}
                              InputLabelProps={{ shrink: true }}
                              value={m.lines}
                              onChange={(e) => updateEditMaterial(idx, 'lines', e.target.value)}
                            />
                          */}
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
                              gap: 1,
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <FormControl fullWidth size="small">
                              <InputLabel>素材種別</InputLabel>
                              <Select
                                label="素材種別"
                                value={m.memoKind}
                                onChange={(e) => {
                                  const nextKind = e.target.value;
                                  updateEditMaterial(idx, 'memoKind', nextKind);
                                  // 添付対象外の種別（記事扱い）に変更した場合は添付ファイルを外す
                                  if (memoKindToSlug(nextKind) === 'article') updateEditMaterial(idx, 'file', null);
                                  // 「記事」以外に変更した場合は本文入力を破棄する
                                  if (!isArticleKind(nextKind)) updateEditMaterial(idx, 'articleBody', '');
                                }}
                              >
                                <MenuItem value="記事">記事</MenuItem>
                                <MenuItem value="写真">写真</MenuItem>
                                <MenuItem value="ＥＰＳ">ＥＰＳ</MenuItem>
                                <MenuItem value="ＰＤＦ">ＰＤＦ</MenuItem>
                                <MenuItem value="エクセル">エクセル</MenuItem>
                                <MenuItem value="動画">動画</MenuItem>
                              </Select>
                            </FormControl>

                            <TextField
                              label="登録予定日"
                              type="date"
                              fullWidth
                              size="small"
                              value={m.registrationDate}
                              onChange={(e) => updateEditMaterial(idx, 'registrationDate', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>

                          {/* 2行目: 仮見出し */}
                          <TextField
                            label="仮見出し"
                            fullWidth
                            size="small"
                            value={m.headline}
                            onChange={(e) => updateEditMaterial(idx, 'headline', e.target.value)}
                            sx={{ mb: 1 }}
                          />

                          {/* 3行目: 関連URL */}
                          <TextField
                            label="関連URL"
                            fullWidth
                            size="small"
                            type="url"
                            value={m.linkUrl}
                            onChange={(e) => updateEditMaterial(idx, 'linkUrl', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 1 }}
                          />

                          {/* 4行目: 編注 */}
                          <TextField
                            label="編注"
                            fullWidth
                            size="small"
                            multiline
                            minRows={1}
                            value={m.editInfo}
                            onChange={(e) => updateEditMaterial(idx, 'editInfo', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />

                          {/* 5行目: 記事本文（素材種別が「記事」のときのみ） */}
                          {isArticleKind(m.memoKind) && (
                            <TextField
                              label="記事本文"
                              fullWidth
                              size="small"
                              multiline
                              minRows={4}
                              value={m.articleBody || ''}
                              onChange={(e) => updateEditMaterial(idx, 'articleBody', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mt: 1 }}
                            />
                          )}

                          {/* 6行目: 素材ファイル添付（写真・ＥＰＳ・ＰＤＦ） */}
                          {memoKindToSlug(m.memoKind) !== 'article' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, mt: 1 }}>
                              <Button
                                component="label"
                                size="small"
                                variant="outlined"
                                startIcon={<AttachFileIcon />}
                                sx={{ flexShrink: 0 }}
                              >
                                ファイル添付
                                <input
                                  type="file"
                                  hidden
                                  accept={MATERIAL_FILE_ACCEPT_BY_SLUG[memoKindToSlug(m.memoKind)] || undefined}
                                  onChange={(e) => {
                                    const f = e.target.files && e.target.files[0];
                                    e.target.value = '';
                                    if (!f) return;
                                    // 写真はJPEGのみ・10MBまで
                                    const errorMessage = validateMaterialFile(memoKindToSlug(m.memoKind), f);
                                    if (errorMessage) {
                                      alert(errorMessage);
                                      return;
                                    }
                                    updateEditMaterial(idx, 'file', f);
                                  }}
                                />
                              </Button>
                              {m.file ? (
                                <>
                                  <Typography
                                    variant="caption"
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {m.file.name}（{m.file.size >= 1024 * 1024
                                      ? `${(m.file.size / (1024 * 1024)).toFixed(1)}MB`
                                      : `${Math.max(1, Math.round(m.file.size / 1024))}KB`}）
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => updateEditMaterial(idx, 'file', null)}
                                    aria-label="clear-file"
                                    sx={{ flexShrink: 0 }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </>
                              ) : m.materialFileKey ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  アップロード済み：{m.materialFileKey.split('/').pop()}（差し替える場合は再添付）
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  未添付
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* 写真のみ添付条件を明示 */}
                          {memoKindToSlug(m.memoKind) === 'photo' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              ※ 写真はJPEG形式（.jpg / .jpeg）・10MBまで
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* === 表示モード（既存） === */}
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedTheme.themeName || '(テーマ名なし)'}
                      </Typography>

                      {/* 作成日時・更新日時 */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>
                        作成日時：{formatDate(selectedTheme.firstCreatedDate)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        更新日時：{formatDate(selectedTheme.lastModifiedDate)}
                      </Typography>

                      {/* テーマ内容 */}
                      {selectedTheme.themeComment && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>
                            テーマ内容：
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {selectedTheme.themeComment}
                          </Typography>
                        </Box>
                      )}

                      {/* 種別・分類・重要度・登録予定日 */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5, mb: 1 }}>
                        {selectedTheme.newsGenre && (
                          <Typography variant="body2" color="text.secondary">
                            種別：{selectedTheme.newsGenre}
                          </Typography>
                        )}
                        {selectedTheme.contents?.[0]?.subjectCode1 && (
                          <Typography variant="body2" color="text.secondary">
                            分類：{SUBJECT_CODE_MAP.find(item => item.code === selectedTheme.contents[0].subjectCode1)?.label || selectedTheme.contents[0].subjectCode1}
                          </Typography>
                        )}
                        {selectedTheme.priority && (
                          <Typography variant="body2" color="text.secondary">
                            重要度：{getPriorityLabel(selectedTheme.priority)}
                          </Typography>
                        )}
                        {selectedTheme.initialRegistrationDate && (
                          <Typography variant="body2" color="text.secondary">
                            登録予定日：{formatDate(selectedTheme.initialRegistrationDate)}
                          </Typography>
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* 素材一覧 */}
                      {contents.map((content, idx) => (
                        <Box
                          key={`${content.childMemoId || 'unknown'}-${idx}`}
                          sx={{
                            mb: 2,
                            pb: 2,
                            borderBottom: idx < contents.length - 1 ? '1px solid #e0e0e0' : 'none',
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                          }}
                        >

                          {/* 左: 素材情報 */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* 素材種別バッジ + ステータス + 仮見出し */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                              <span style={memoKindBadgeStyle}>
                                {getMemoKindLabel(content.memoKind)}
                              </span>
                              {content.publishingStatus && (
                                <span style={{
                                  ...statusBadgeStyle,
                                  backgroundColor: badgeColors.publishingStatus,
                                }}>
                                  {content.publishingStatus}
                                </span>
                              )}
                              {content.mediaStatus && content.mediaStatus !== '未派生' && (
                                <span style={{
                                  ...statusBadgeStyle,
                                  backgroundColor: badgeColors.mediaStatus,
                                  color: '#404048',
                                }}>
                                  {content.mediaStatus}
                                </span>
                              )}
                              <Typography variant="body1" sx={{ fontWeight: 500, ml: 0.5 }}>
                                {content.headLine || '(見出しなし)'}
                              </Typography>
                            </Box>

                            {/* 素材の登録予定日・関連URL・編注 */}
                            <Box sx={{ pl: '60px' }}>
                              {content.registrationDate && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>
                                  登録予定日：{formatDate(content.registrationDate)}
                                </Typography>
                              )}
                              {content.linkUrl && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, wordBreak: 'break-all' }}>
                                  関連URL：{content.linkUrl}
                                </Typography>
                              )}
                              {content.editInfo && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  編注：{content.editInfo}
                                </Typography>
                              )}
                              {isArticleKind(content.memoKind) && articleBodyMap[content.childMemoId] && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>
                                    記事本文：
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {articleBodyMap[content.childMemoId]}
                                  </Typography>
                                </Box>
                              )}
                              {attachmentMap[content.childMemoId] && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, wordBreak: 'break-all' }}>
                                  添付ファイル：
                                  <Box
                                    component="span"
                                    onClick={() => downloadMaterialFile(attachmentMap[content.childMemoId]).catch((e) => {
                                      console.error('添付ファイルダウンロードエラー:', e);
                                      alert('添付ファイルのダウンロードに失敗しました');
                                    })}
                                    sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                                  >
                                    {attachmentMap[content.childMemoId].split('/').pop()}
                                  </Box>
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* 右: 掲載情報（素材単位） */}
                          {(content.publishingStatus === '掲載済み' || content.publishingStatus === '降版済み' || content.publishingStatus === '出稿済み') &&
                            content.publishingInfoList && content.publishingInfoList.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flexShrink: 0 }}>
                                {content.publishingInfoList.map((info, pubIdx) => (
                                  <Box
                                    key={pubIdx}
                                    sx={{
                                      backgroundColor: badgeColors.publishingInfo,
                                      color: '#fff',
                                      borderRadius: '4px',
                                      padding: '2px 8px',
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      whiteSpace: 'nowrap',
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {toHalfWidth(formatDate(info.publishingDate))}{info.publishingPage ? ` ${toHalfWidth(info.publishingPage)}` : ''}
                                  </Box>
                                ))}
                              </Box>
                            )}
                        </Box>
                      ))}

                    </>
                  )}
                </Box>
              </DialogContent>
            );
          })()}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default MemoPage;
